import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/api';
import { useAuth } from '../context/AuthContext';
import {
  ListPageShell, ListPageHeader, ListSearchBar, ListPagination,
} from '../components/ui/ListPageChrome';

const statusStyle = (s) => ({
  'Posted':    'bg-[#E8F4FE] text-[#2184F2]',
  'Cancelled': 'bg-[#F0F4F3] text-[#6B7F7F]',
}[s] || 'bg-[#EDF2F7] text-[#4A6FA5]');

export default function IssueForProduction() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isSuperAdmin = user?.role_name === 'Super Admin';

  const [issues, setIssues]      = useState([]);
  const [loading, setLoading]    = useState(true);
  const [search, setSearch]      = useState('');
  const [page, setPage]          = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal]        = useState(0);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [isAllSelected, setIsAllSelected] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const PER_PAGE = 25;

  const loadIssues = useCallback(async () => {
    try {
      setLoading(true);
      let url = `/production-orders/all-issues?page=${page}&limit=${PER_PAGE}`;
      if (search) url += `&search=${encodeURIComponent(search)}`;
      const data = await api.get(url);
      setIssues(data.rows || []);
      setTotalPages(data.totalPages || 1);
      setTotal(data.total || 0);
      setSelectedIds(new Set());
      setIsAllSelected(false);
    } catch { setIssues([]); }
    finally { setLoading(false); }
  }, [page, search, refreshKey]);

  useEffect(() => { loadIssues(); }, [loadIssues]);

  const applySearch = (v) => { setSearch(v); setPage(1); };

  const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '';
  const fmtAmt = (n) => Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const handleSelectRow = (id) => {
    if (isAllSelected) {
      const next = new Set();
      issues.forEach(iss => {
        if (iss.issue_id !== id) {
          next.add(iss.issue_id);
        }
      });
      setSelectedIds(next);
      setIsAllSelected(false);
    } else {
      setSelectedIds(prev => {
        const next = new Set(prev);
        if (next.has(id)) {
          next.delete(id);
        } else {
          next.add(id);
        }
        return next;
      });
    }
  };

  const handleSelectAll = () => {
    const pageAllSelected = issues.length > 0 && issues.every(iss => selectedIds.has(iss.issue_id));
    if (isAllSelected || pageAllSelected) {
      setIsAllSelected(false);
      setSelectedIds(new Set());
    } else {
      setIsAllSelected(true);
      setSelectedIds(new Set());
    }
  };

  const handleBulkDelete = async () => {
    const count = isAllSelected ? total : selectedIds.size;
    if (count === 0) return;
    const msg = `Are you sure you want to delete the ${count} selected issue(s)? This will also revert stock and component quantities.`;
    if (!window.confirm(msg)) return;

    try {
      if (isAllSelected) {
        await api.post('/production-orders/issues/bulk-delete', {
          all: true,
          search: search || undefined
        });
      } else {
        await api.post('/production-orders/issues/bulk-delete', {
          ids: Array.from(selectedIds)
        });
      }
      setSelectedIds(new Set());
      setIsAllSelected(false);

      const newTotal = total - count;
      const newMaxPages = Math.ceil(newTotal / PER_PAGE) || 1;
      if (page > newMaxPages) {
        setPage(newMaxPages);
      } else {
        setRefreshKey(prev => prev + 1);
      }
    } catch (err) {
      alert(err.message || 'Failed to delete issues');
    }
  };

  const displayCount = isAllSelected ? total : selectedIds.size;

  return (
    <ListPageShell>
      <ListPageHeader
        title="Issue for Production"
        subtitle={`${total} record${total !== 1 ? 's' : ''}`}
        action={
          <div className="flex items-center gap-2">
            {isSuperAdmin && displayCount > 0 && (
              <button type="button" onClick={handleBulkDelete} className="btn-danger">
                Delete Selected ({displayCount})
              </button>
            )}
            <button type="button" onClick={() => navigate('/production/issue/new')} className="btn-primary">
              + Add New
            </button>
          </div>
        }
      />

      <ListSearchBar value={search} onChange={applySearch} placeholder="Search issue #, order #, item…" />

      <div className="data-table-wrap">
        <table className="w-full text-sm">
          <thead>
            <tr className="table-head">
              {isSuperAdmin && (
                <th className="text-center px-4 py-3 w-12">
                  <input
                    type="checkbox"
                    checked={isAllSelected || (issues.length > 0 && issues.every(iss => selectedIds.has(iss.issue_id)))}
                    onChange={handleSelectAll}
                    className="rounded border-gray-300 text-sap-blue focus:ring-sap-blue cursor-pointer"
                  />
                </th>
              )}
              <th className="text-left px-4 py-3 font-semibold text-2xs uppercase tracking-wider">Issue #</th>
              <th className="text-left px-4 py-3 font-semibold text-2xs uppercase tracking-wider">Order #</th>
              <th className="text-left px-4 py-3 font-semibold text-2xs uppercase tracking-wider">Product</th>
              <th className="text-center px-4 py-3 font-semibold text-2xs uppercase tracking-wider">Posting Date</th>
              <th className="text-center px-4 py-3 font-semibold text-2xs uppercase tracking-wider">Lines</th>
              <th className="text-right px-4 py-3 font-semibold text-2xs uppercase tracking-wider">Total Amount</th>
              <th className="text-center px-4 py-3 font-semibold text-2xs uppercase tracking-wider">Status</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={isSuperAdmin ? 8 : 7} className="px-4 py-8 text-center text-sap-textLight">Loading…</td></tr>
            ) : issues.length === 0 ? (
              <tr><td colSpan={isSuperAdmin ? 8 : 7} className="px-4 py-8 text-center text-sap-textLight">No issues found.</td></tr>
            ) : issues.map((iss) => (
              <tr
                key={iss.issue_id}
                onClick={() => navigate(`/production/issue/${iss.issue_id}`)}
                className="table-row even:bg-gray-50/50"
              >
                {isSuperAdmin && (
                  <td className="px-4 py-3 text-center w-12" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={isAllSelected || selectedIds.has(iss.issue_id)}
                      onChange={() => handleSelectRow(iss.issue_id)}
                      className="rounded border-gray-300 text-sap-blue focus:ring-sap-blue cursor-pointer"
                    />
                  </td>
                )}
                <td className="px-4 py-3 font-semibold text-sap-blue">{iss.issue_number}</td>
                <td className="px-4 py-3 font-semibold">{iss.order_no}</td>
                <td className="px-4 py-3">{iss.item_no}{iss.product_desc ? ` — ${iss.product_desc}` : ''}</td>
                <td className="px-4 py-3 text-center text-sap-textLight">{fmtDate(iss.posting_date)}</td>
                <td className="px-4 py-3 text-center text-sap-textLight">{iss.line_count}</td>
                <td className="px-4 py-3 text-right font-semibold">{fmtAmt(iss.total_amount)}</td>
                <td className="px-4 py-3 text-center">
                  {iss.status === 'Posted' ? (
                    <span className="badge-active">{iss.status}</span>
                  ) : (
                    <span className={`px-2.5 py-0.5 rounded-lg text-xs font-semibold ${statusStyle(iss.status)}`}>{iss.status}</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <ListPagination page={page} totalPages={totalPages} total={total} onPageChange={setPage} />
    </ListPageShell>
  );
}
